import {
  isNonEmptyString,
  isNonEmptyStringArray,
  isNumber,
  isObject,
  isServiceProvider,
  ServiceAuthType,
  ServiceProvider,
} from '../../..';

export interface CredentialEntity {
  authType: ServiceAuthType;
  provider: ServiceProvider;
  accessToken: string;
  /** UNIX timestamp in milliseconds when the `accessToken` expires at. */
  expiresAt: number;
  refreshToken: string;
  scopes: string[];
  metadata: Record<string, any>;
}

export interface DropboxCredential {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
}

export interface GoogleCredential {
  access_token: string;
  expiry_date: number;
  refresh_token: string;
  scope: string;
}

export interface MicrosoftCredential {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
}

export interface ZohoCredential {
  access_token: string;
  expires_in: number;
  /** Optional — Zoho only returns refresh_token on the first authorization */
  refresh_token?: string;
  scope: string;
  api_domain: string;
  /** Optional — only the server-based (code) flow returns accounts_server; implicit flow omits it. */
  accounts_server?: string;
}

export function assertIsCredentialEntity(value: any): asserts value is CredentialEntity {
  if (!isObject(value)) {
    throw new Error(`'credential' must be an object`);
  }
  if (!('provider' in value && isServiceProvider(value.provider))) {
    throw new Error(`'credential.provider' must be a valid 'ServiceProvider'`);
  }
  if (!('accessToken' in value && isNonEmptyString(value.accessToken))) {
    throw new Error(`'credential.accessToken' must be a non-empty string`);
  }
  if (!('expiresAt' in value && isNumber(value.expiresAt))) {
    throw new Error(`'credential.expiresAt' must be a number`);
  }
  if (!('refreshToken' in value && typeof value.refreshToken === 'string')) {
    // Empty string is allowed: Zoho's implicit/client-based flow does not issue a refresh_token.
    throw new Error(`'credential.refreshToken' must be a string`);
  }
  if (!('scopes' in value && isNonEmptyStringArray(value.scopes))) {
    throw new Error(`'credential.scopes' must be a non-empty string array`);
  }
  if (!('metadata' in value && isObject(value.metadata))) {
    throw new Error(`'credential.metadata' must be an object`);
  }
}
